/**
 * "12 minutes ago", "Yesterday", "3 Sep 14:22" — with the full date on hover.
 *
 * Separate from `when()`, which answers a different question. `when()` labels a
 * *scheduled* moment and reads in both directions ("Tomorrow 09:30"); this one
 * labels something that has already happened, where an elapsed time is what a
 * reader actually wants for anything recent.
 *
 * Every result carries a `title` as well as its text, because a relative label
 * throws information away. "Yesterday" is the right thing to read and the wrong
 * thing to have to reason from, so the exact moment stays one hover away rather
 * than being lost.
 */

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export interface Ago {
  /** What the row shows. */
  text: string;
  /** The full moment, for a `title` attribute. */
  title: string;
}

/**
 * Whole calendar days from `from` to `to`, signed — negative for the past.
 *
 * Collapsed to local midnight first, so this is a difference of dates rather
 * than of instants: 23:50 last night and 00:10 this morning are one day apart,
 * not zero minutes. `Math.round` rather than a floor, because a day crossing a
 * clock change is 23 or 25 hours long.
 */
function calendarDaysApart(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / DAY);
}

/** "Thursday, 27 August 2026 at 17:09" — unambiguous, for the tooltip. */
function fullMoment(date: Date): string {
  const day = date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return `${day} at ${time}`;
}

/**
 * Label a past moment.
 *
 * Today is elapsed time, because "17:09" makes a reader do the subtraction and
 * "12 minutes ago" is the thing they were going to work out. Yesterday is named,
 * because an elapsed time in the twenties of hours is harder to place than the
 * word. Anything older is a date, because by then the elapsed figure has stopped
 * being useful and the date has started being.
 *
 * A moment in the future is possible — a clock a little ahead of ours, a
 * timestamp from another machine — and is reported as "just now" rather than as a
 * negative age. Being a few seconds out is not worth a reader seeing "in 4
 * seconds" on a call that has already ended.
 */
export function ago(iso: string, now: Date = new Date()): Ago {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { text: '—', title: '' };

  const title = fullMoment(date);
  const elapsed = now.getTime() - date.getTime();
  const days = calendarDaysApart(now, date);

  if (days === 0) {
    if (elapsed < MINUTE) return { text: 'just now', title };

    if (elapsed < HOUR) {
      const minutes = Math.floor(elapsed / MINUTE);
      return { text: `${String(minutes)} minute${minutes === 1 ? '' : 's'} ago`, title };
    }

    const hours = Math.floor(elapsed / HOUR);
    return { text: `${String(hours)} hour${hours === 1 ? '' : 's'} ago`, title };
  }

  if (days === -1) return { text: 'Yesterday', title };

  // The year only when it is not this one: "3 Sep" is unambiguous within the
  // year, and "3 Sep 2026" is noise until it is not.
  const sameYear = date.getFullYear() === now.getFullYear();
  const day = date.toLocaleDateString(
    'en-GB',
    sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' },
  );
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return { text: `${day} ${time}`, title };
}
